import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'es' | 'en';

export interface Translations {
  nav: {
    home: string;
    dashboard: string;
    pricing: string;
    guides: string;
    howItWorks: string;
    privacy: string;
    terms: string;
    upgrade: string;
    signIn: string;
    signOut: string;
    settings: string;
    today: string;
    of: string;
  };
  hero: {
    betaBadge: string;
    title1: string;
    title2: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    stepsTitle: string;
    step1Title: string;
    step1Desc: string;
    step2Title: string;
    step2Desc: string;
    step3Title: string;
    step3Desc: string;
  };
  uploader: {
    title: string;
    subtitle: string;
    dragDrop: string;
    formats: string;
    limit: string;
    uploading: string;
  };
  settings: {
    title: string;
    subtitle: string;
    instructions: string;
    instructionsPlaceholder: string;
    presetsLabel: string;
    presetBalanced: string;
    presetDynamic: string;
    presetKeyPoints: string;
    presetNoSilence: string;
    targetDuration: string;
    estimatedSaved: string;
    submitBtn: string;
  };
  render: {
    successTitle: string;
    renderingTitle: string;
    failedTitle: string;
    successDesc: string;
    renderingDesc: string;
    downloadBtn: string;
    newJobBtn: string;
    tabSummary: string;
    tabOriginal: string;
    progressLabel: string;
  };
  pricing: {
    title: string;
    subtitle: string;
    freeTitle: string;
    freePrice: string;
    freeDesc: string;
    freeBtn: string;
    premiumTitle: string;
    premiumPrice: string;
    premiumDesc: string;
    premiumBtn: string;
  };
  footer: {
    tagline: string;
    rights: string;
    product: string;
    resources: string;
    legal: string;
    social: string;
  };
}

const translations: Record<Language, Translations> = {
  es: {
    nav: {
      home: 'Inicio',
      dashboard: 'Panel',
      pricing: 'Planes',
      guides: 'Guías & Blog',
      howItWorks: 'Cómo funciona',
      privacy: 'Privacidad & RGPD',
      terms: 'Términos',
      upgrade: 'Mejorar',
      signIn: 'Iniciar sesión',
      signOut: 'Cerrar sesión',
      settings: 'Configuración',
      today: 'Hoy:',
      of: 'de'
    },
    hero: {
      betaBadge: 'BETA PÚBLICA 1.0',
      title1: 'Resume tus vídeos.',
      title2: 'Solo lo que importa.',
      subtitle: 'La IA analiza el contenido, detecta los momentos clave y genera un corte limpio en segundos. Sin rodeos, sin pérdida de calidad.',
      ctaPrimary: 'Probar gratis ahora',
      ctaSecondary: 'Ver planes',
      stepsTitle: 'Flujo de trabajo en 3 pasos',
      step1Title: 'Sube tu vídeo',
      step1Desc: 'Arrastra tu archivo MP4, MOV o WebM. Procesamiento seguro en tu navegador.',
      step2Title: 'La IA extrae lo clave',
      step2Desc: 'Transcripción y detección de fragmentos relevantes respetando las frases completas.',
      step3Title: 'Descarga el corte final',
      step3Desc: 'Ensamblado cuadro a cuadro con cortes limpios y audio suavizado.'
    },
    uploader: {
      title: 'Toca aquí para seleccionar tu vídeo',
      subtitle: 'Listo para procesar',
      dragDrop: 'o arrastra y suelta el archivo',
      formats: 'MP4 · MOV · WebM',
      limit: 'Límite:',
      uploading: 'Subiendo vídeo...'
    },
    settings: {
      title: 'Configura tu resumen',
      subtitle: 'Elige el objetivo de duración e indica a la IA qué temas priorizar.',
      instructions: 'Instrucciones para la IA',
      instructionsPlaceholder: 'Ej: Destaca las explicaciones principales y omite los descansos o anécdotas...',
      presetsLabel: 'Plantillas rápidas:',
      presetBalanced: '🎯 Resumen equilibrado',
      presetDynamic: '⚡ Dinámico y ágil',
      presetKeyPoints: '💡 Puntos clave',
      presetNoSilence: '✂️ Eliminar pausas',
      targetDuration: 'Duración objetivo del resumen',
      estimatedSaved: 'Ahorro estimado:',
      submitBtn: 'Continuar al plan de cortes'
    },
    render: {
      successTitle: 'Resumen renderizado con éxito',
      renderingTitle: 'Renderizando y ensamblando vídeo con cortes exactos...',
      failedTitle: 'Error en el renderizado',
      successDesc: 'Vídeo reducido con cortes limpios aplicados sobre tu metraje original.',
      renderingDesc: 'Procesando los fragmentos seleccionados para crear el archivo final descargable.',
      downloadBtn: 'Descargar editado',
      newJobBtn: 'Nuevo resumen',
      tabSummary: 'Resumen',
      tabOriginal: 'Original',
      progressLabel: 'Progreso de edición'
    },
    pricing: {
      title: 'Precios claros. Sin sorpresas.',
      subtitle: 'Comienza gratis con 1 resumen al día o accede a procesamiento prioritario con el plan Premium.',
      freeTitle: 'Plan Gratuito',
      freePrice: '0€',
      freeDesc: 'Ideal para probar la herramienta con vídeos cotidianos.',
      freeBtn: 'Empezar gratis',
      premiumTitle: 'Plan Premium',
      premiumPrice: '14,99€',
      premiumDesc: 'Para creadores de contenido, educadores y profesionales exigentes.',
      premiumBtn: 'Suscribirse a Premium'
    },
    footer: {
      tagline: 'Herramienta de resumen inteligente de vídeo y podcast mediante IA y cortes de precisión.',
      rights: 'Todos los derechos reservados.',
      product: 'Producto',
      resources: 'Recursos',
      legal: 'Legal',
      social: 'Comunidad'
    }
  },
  en: {
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      pricing: 'Pricing',
      guides: 'Guides & Blog',
      howItWorks: 'How it works',
      privacy: 'Privacy & GDPR',
      terms: 'Terms',
      upgrade: 'Upgrade',
      signIn: 'Sign in',
      signOut: 'Sign out',
      settings: 'Settings',
      today: 'Today:',
      of: 'of'
    },
    hero: {
      betaBadge: 'PUBLIC BETA 1.0',
      title1: 'Summarize videos.',
      title2: 'Keep what matters.',
      subtitle: 'AI analyzes speech, detects key moments, and generates a clean cut in seconds. No fluff, zero quality loss.',
      ctaPrimary: 'Try for free now',
      ctaSecondary: 'View pricing',
      stepsTitle: '3-step workflow',
      step1Title: 'Upload your video',
      step1Desc: 'Drop your MP4, MOV, or WebM file. Secure processing right in your browser.',
      step2Title: 'AI extracts the highlights',
      step2Desc: 'Speech transcription and key moment detection respecting natural sentence breaks.',
      step3Title: 'Download the final cut',
      step3Desc: 'Frame-accurate assembly with clean crossfades and smooth audio.'
    },
    uploader: {
      title: 'Click here to choose your video',
      subtitle: 'Ready to process',
      dragDrop: 'or drag and drop your file',
      formats: 'MP4 · MOV · WebM',
      limit: 'Limit:',
      uploading: 'Uploading video...'
    },
    settings: {
      title: 'Configure your summary',
      subtitle: 'Set target duration and tell the AI what topics to prioritize.',
      instructions: 'AI Instructions',
      instructionsPlaceholder: 'E.g., Highlight core explanations and skip intro banter or pauses...',
      presetsLabel: 'Quick presets:',
      presetBalanced: '🎯 Balanced summary',
      presetDynamic: '⚡ Fast & punchy',
      presetKeyPoints: '💡 Key takeaways',
      presetNoSilence: '✂️ Remove pauses',
      targetDuration: 'Target summary length',
      estimatedSaved: 'Estimated time saved:',
      submitBtn: 'Continue to cut plan'
    },
    render: {
      successTitle: 'Summary rendered successfully',
      renderingTitle: 'Rendering and assembling video with precision cuts...',
      failedTitle: 'Rendering failed',
      successDesc: 'Video trimmed with clean frame-accurate cuts applied to your original footage.',
      renderingDesc: 'Processing selected clips to create your final downloadable video.',
      downloadBtn: 'Download edited video',
      newJobBtn: 'New summary',
      tabSummary: 'Summary',
      tabOriginal: 'Original',
      progressLabel: 'Editing progress'
    },
    pricing: {
      title: 'Transparent pricing. No surprises.',
      subtitle: 'Start free with 1 summary per day or unlock priority processing with the Premium plan.',
      freeTitle: 'Free Plan',
      freePrice: '$0',
      freeDesc: 'Perfect for trying the tool on everyday videos.',
      freeBtn: 'Get started free',
      premiumTitle: 'Premium Plan',
      premiumPrice: '$14.99',
      premiumDesc: 'For content creators, educators, and demanding professionals.',
      premiumBtn: 'Subscribe to Premium'
    },
    footer: {
      tagline: 'AI-powered video and podcast summarizer with frame-accurate precision cuts.',
      rights: 'All rights reserved.',
      product: 'Product',
      resources: 'Resources',
      legal: 'Legal',
      social: 'Community'
    }
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'es',
  setLanguage: () => {},
  t: translations.es
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'es';
    
    // Check URL query parameter ?lang=en or ?lang=es
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang === 'en' || urlLang === 'es') return urlLang;

    // Check localStorage
    const saved = localStorage.getItem('vidly_lang');
    if (saved === 'en' || saved === 'es') return saved;

    // Fallback to browser language
    if (navigator.language && navigator.language.toLowerCase().startsWith('en')) {
      return 'en';
    }
    return 'es';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vidly_lang', lang);
      document.documentElement.lang = lang;
      
      // Update page title and description dynamically
      if (lang === 'en') {
        document.title = 'Vidly AI — Smart Video Summarizer & Precision Video Cutter';
      } else {
        document.title = 'Vidly AI — Resumen Inteligente de Vídeo';
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
